import React, { useEffect, useState, useMemo } from "react";
import { api, rupiah, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Search, Plus, Minus, Trash2, ShoppingCart, X, Package, CheckCircle2 } from "lucide-react";

export default function POS() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("all");
  const [cart, setCart] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [customerId, setCustomerId] = useState("none");
  const [payMethod, setPayMethod] = useState("cash");
  const [paid, setPaid] = useState("");
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => setCustomers([]));
  };
  useEffect(load, []);

  const filtered = products.filter((p) => {
    if (cat !== "all" && p.category_id !== cat) return false;
    if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const addToCart = (p) => {
    if (p.stock <= 0) { toast.error("Stok habis"); return; }
    setCart((c) => {
      const ex = c.find((i) => i.id === p.id);
      if (ex) {
        if (ex.qty >= p.stock) { toast.error("Melebihi stok tersedia"); return c; }
        return c.map((i) => (i.id === p.id ? { ...i, qty: i.qty + 1 } : i));
      }
      return [...c, { id: p.id, name: p.name, price: p.price, qty: 1, stock: p.stock }];
    });
  };

  const changeQty = (id, delta) => {
    setCart((c) => c.map((i) => {
      if (i.id !== id) return i;
      const q = i.qty + delta;
      if (q > i.stock) { toast.error("Melebihi stok"); return i; }
      return { ...i, qty: q };
    }).filter((i) => i.qty > 0));
  };

  const removeItem = (id) => setCart((c) => c.filter((i) => i.id !== id));

  const subtotal = useMemo(() => cart.reduce((s, i) => s + i.price * i.qty, 0), [cart]);
  const total = Math.max(0, subtotal - (Number(discount) || 0));
  const change = paid ? Number(paid) - total : 0;

  const openCheckout = () => {
    if (cart.length === 0) { toast.error("Keranjang masih kosong"); return; }
    setPaid(String(total));
    setCheckoutOpen(true);
  };

  const submit = async () => {
    if (payMethod === "cash" && Number(paid) < total) { toast.error("Jumlah bayar kurang"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/transactions", {
        items: cart.map((i) => ({ product_id: i.id, qty: i.qty })),
        customer_id: customerId === "none" ? null : customerId,
        discount: Number(discount) || 0,
        payment_method: payMethod,
        paid: Number(paid) || total,
      });
      setReceipt(data);
      setCheckoutOpen(false);
      setCart([]);
      setDiscount(0);
      setPaid("");
      setCustomerId("none");
      load();
      toast.success("Transaksi berhasil!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-[1fr_360px] gap-6" data-testid="pos-page">
      {/* Products */}
      <div>
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari produk..."
              className="pl-9 rounded-lg" data-testid="pos-search-input" />
          </div>
          <Select value={cat} onValueChange={setCat}>
            <SelectTrigger className="w-full sm:w-44 rounded-lg" data-testid="pos-category-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Kategori</SelectItem>
              {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
          {filtered.map((p) => (
            <button
              key={p.id}
              onClick={() => addToCart(p)}
              disabled={p.stock <= 0}
              data-testid={`pos-product-${p.id}`}
              className="text-left p-3 rounded-xl border border-slate-200 bg-white hover:shadow-md hover:-translate-y-1 transition-all duration-200 active:scale-95 disabled:opacity-40 disabled:hover:translate-y-0"
            >
              <div className="aspect-square rounded-lg bg-accent/50 flex items-center justify-center mb-2 overflow-hidden">
                {p.image ? <img src={p.image} alt="" className="w-full h-full object-cover" /> : <Package className="w-8 h-8 text-primary/40" />}
              </div>
              <div className="text-sm font-semibold leading-tight line-clamp-2">{p.name}</div>
              <div className="mt-1 text-primary font-bold text-sm">{rupiah(p.price)}</div>
              <div className={`text-xs mt-0.5 ${p.stock <= p.low_stock_threshold ? "text-amber-600" : "text-muted-foreground"}`}>Stok: {p.stock}</div>
            </button>
          ))}
          {filtered.length === 0 && <div className="col-span-full text-center py-16 text-muted-foreground">Tidak ada produk.</div>}
        </div>
      </div>

      {/* Cart */}
      <Card className="p-4 border-slate-200 rounded-xl shadow-sm lg:sticky lg:top-20 h-fit lg:max-h-[calc(100vh-6rem)] flex flex-col" data-testid="pos-cart">
        <div className="flex items-center gap-2 pb-3 border-b border-slate-200">
          <ShoppingCart className="w-5 h-5 text-primary" />
          <h3 className="font-heading font-bold">Keranjang</h3>
          <span className="ml-auto text-sm text-muted-foreground">{cart.length} item</span>
        </div>

        <div className="flex-1 overflow-y-auto kasir-scroll py-3 space-y-2 min-h-[120px]">
          {cart.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">Keranjang kosong. Klik produk untuk menambah.</p>}
          {cart.map((i) => (
            <div key={i.id} className="flex items-center gap-2" data-testid={`cart-item-${i.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{i.name}</div>
                <div className="text-xs text-muted-foreground">{rupiah(i.price)}</div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => changeQty(i.id, -1)} className="w-6 h-6 rounded bg-slate-100 flex items-center justify-center hover:bg-slate-200"><Minus className="w-3 h-3" /></button>
                <span className="w-6 text-center text-sm font-semibold">{i.qty}</span>
                <button onClick={() => changeQty(i.id, 1)} className="w-6 h-6 rounded bg-slate-100 flex items-center justify-center hover:bg-slate-200"><Plus className="w-3 h-3" /></button>
              </div>
              <button onClick={() => removeItem(i.id)} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>

        <div className="pt-3 border-t border-slate-200 space-y-2">
          <div className="flex justify-between text-sm"><span className="text-muted-foreground">Subtotal</span><span className="font-medium">{rupiah(subtotal)}</span></div>
          <div className="flex justify-between items-center text-sm">
            <span className="text-muted-foreground">Diskon</span>
            <Input type="number" value={discount} onChange={(e) => setDiscount(e.target.value)} className="w-28 h-8 text-right rounded-lg" data-testid="pos-discount-input" />
          </div>
          <div className="flex justify-between text-lg font-bold pt-1"><span>Total</span><span className="text-primary">{rupiah(total)}</span></div>
          <Button onClick={openCheckout} className="w-full rounded-lg h-11 mt-2 active:scale-95 transition-transform" data-testid="pos-checkout-btn">
            Bayar Sekarang
          </Button>
        </div>
      </Card>

      {/* Checkout dialog */}
      <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
        <DialogContent data-testid="checkout-dialog">
          <DialogHeader><DialogTitle className="font-heading">Pembayaran</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex justify-between text-2xl font-bold"><span>Total</span><span className="text-primary">{rupiah(total)}</span></div>
            <div>
              <label className="text-sm font-medium">Pelanggan (opsional)</label>
              <Select value={customerId} onValueChange={setCustomerId}>
                <SelectTrigger className="mt-1 rounded-lg" data-testid="checkout-customer"><SelectValue placeholder="Pilih pelanggan" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Umum / Tanpa pelanggan</SelectItem>
                  {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Metode Pembayaran</label>
              <Select value={payMethod} onValueChange={setPayMethod}>
                <SelectTrigger className="mt-1 rounded-lg" data-testid="checkout-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Tunai</SelectItem>
                  <SelectItem value="qris">QRIS</SelectItem>
                  <SelectItem value="transfer">Transfer Bank</SelectItem>
                  <SelectItem value="debit">Kartu Debit/Kredit</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {payMethod === "cash" && (
              <div>
                <label className="text-sm font-medium">Jumlah Bayar</label>
                <Input type="number" value={paid} onChange={(e) => setPaid(e.target.value)} className="mt-1 rounded-lg" data-testid="checkout-paid" />
                <div className="mt-1 text-sm text-muted-foreground">Kembalian: <span className="font-semibold text-slate-800">{rupiah(change > 0 ? change : 0)}</span></div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCheckoutOpen(false)} className="rounded-lg">Batal</Button>
            <Button onClick={submit} disabled={loading} className="rounded-lg" data-testid="checkout-confirm-btn">Selesaikan Transaksi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receipt */}
      <Dialog open={!!receipt} onOpenChange={() => setReceipt(null)}>
        <DialogContent data-testid="receipt-dialog">
          <div className="text-center">
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
            <h3 className="font-heading text-xl font-bold mt-3">Transaksi Berhasil</h3>
            <p className="text-sm text-muted-foreground">{receipt?.invoice_no}</p>
          </div>
          <div className="border-t border-dashed border-slate-300 my-2 pt-3 space-y-1 text-sm">
            {receipt?.items?.map((i, x) => (
              <div key={x} className="flex justify-between">
                <span>{i.name} × {i.qty}</span><span>{rupiah(i.subtotal)}</span>
              </div>
            ))}
            <div className="flex justify-between border-t border-slate-200 pt-2 mt-2 font-bold text-base"><span>Total</span><span>{rupiah(receipt?.total)}</span></div>
            {receipt?.payment_method === "cash" && (
              <>
                <div className="flex justify-between text-muted-foreground"><span>Bayar</span><span>{rupiah(receipt?.paid)}</span></div>
                <div className="flex justify-between text-muted-foreground"><span>Kembalian</span><span>{rupiah(receipt?.change)}</span></div>
              </>
            )}
          </div>
          <Button onClick={() => setReceipt(null)} className="rounded-lg w-full" data-testid="receipt-close-btn">Transaksi Baru</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
